const API_BASE =
    import.meta.env.VITE_API_URL ||
    'https://offpricemodule.storage.smart-sale.moscow/api';

export interface ApiResponse<T = any> {
    success: boolean;
    data: T;
    error_msg: string | null;
}

class ApiError extends Error {
    status: number;
    constructor(status: number, message: string) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
    }
}

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
    return request<T>(path, options);
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE}${path}`;

    const headers: Record<string, string> = {
        Accept: 'application/json',
        ...((options.headers as Record<string, string>) || {}),
    };

    // Don't set Content-Type for FormData (browser sets boundary automatically)
    if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }

    // Pass user identity header
    const userLogin = localStorage.getItem('user_login') || 'unknown';
    headers['X-User-Login'] = userLogin;

    const res = await fetch(url, { ...options, headers, credentials: 'include' });

    if (!res.ok) {
        if (res.status === 401) {
            window.dispatchEvent(new CustomEvent('b24-unauthorized'));
        }
        let errorMsg = `HTTP ${res.status}`;
        try {
            const body: ApiResponse = await res.json();
            if (body.error_msg) errorMsg = body.error_msg;
        } catch {}
        throw new ApiError(res.status, errorMsg);
    }

    const body: ApiResponse<T> = await res.json();

    if (!body.success) {
        throw new ApiError(422, body.error_msg || 'Неизвестная ошибка');
    }

    return body.data;
}

// ── References ────────────────────────────────────────────

export const api = {
    // References
    getReferences: () => request<any>('/references'),

    // Offers
    getOffers: (params?: Record<string, string>) => {
        const qs = params ? '?' + new URLSearchParams(params).toString() : '';
        return request<any[]>(`/offers${qs}`);
    },
    getOffer: (code: number) => request<any>(`/offers/${code}`),
    createOffer: (data: any) =>
        request<any>('/offers', { method: 'POST', body: JSON.stringify(data) }),
    updateOffer: (code: number, data: any) =>
        request<any>(`/offers/${code}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),
    getOfferColumns: (code: number) =>
        request<any[]>(`/offers/${code}/columns`),
    hideOffer: (code: number) =>
        request<any>(`/offers/${code}/hide`, { method: 'POST' }),
    restoreOffer: (code: number) =>
        request<any>(`/offers/${code}/restore`, { method: 'POST' }),
    getHiddenOffers: () => request<any[]>('/offers/hidden'),

    // Offer rows (coordinator)
    getOfferRows: (code: number, params?: Record<string, string>) => {
        const qs = params ? '?' + new URLSearchParams(params).toString() : '';
        return request<any[]>(`/offers/${code}/rows${qs}`);
    },
    saveOfferRows: (code: number, data: { columns: any[]; rows: any[] }) =>
        request<any[]>(`/offers/${code}/rows`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),
    coordinatorFinish: (code: number) =>
        request<any>(`/offers/${code}/coordinator-finish`, { method: 'POST' }),
    changeWareGroup: (code: number, rowCode: number, wareGroupCode: number) =>
        request<any>(`/offers/${code}/rows/${rowCode}/change-ware-group`, {
            method: 'POST',
            body: JSON.stringify({ ware_group_code: wareGroupCode }),
        }),

    // Buyer selections
    getOfferBuyers: (code: number, params?: Record<string, string>) => {
        const qs = params ? '?' + new URLSearchParams(params).toString() : '';
        return request<any[]>(`/offers/${code}/buyers${qs}`);
    },
    getBuyerSelections: (
        code: number,
        buyerCode: number,
        params?: Record<string, string>,
    ) => {
        const qs = params ? '?' + new URLSearchParams(params).toString() : '';
        return request<any[]>(
            `/offers/${code}/buyers/${buyerCode}/selections${qs}`,
        );
    },
    saveBuyerSelections: (code: number, buyerCode: number, rows: any[]) =>
        request<any[]>(`/offers/${code}/buyers/${buyerCode}/selections`, {
            method: 'POST',
            body: JSON.stringify({ rows }),
        }),
    finishBuyer: (code: number, buyerCode: number) =>
        request<any>(`/offers/${code}/buyers/${buyerCode}/finish`, {
            method: 'POST',
        }),
    loadVendor: (code: number, rows: any[]) =>
        request<any>(`/offers/${code}/load-vendor`, {
            method: 'POST',
            body: JSON.stringify({ rows }),
        }),

    // History & reports (tooltips)
    getBuyerSelectHistory: (code: number, selectCode: number) =>
        request<any[]>(`/offers/${code}/buyer-select-history/${selectCode}`),
    getVendorBackground: (vendorCode: number) =>
        request<any[]>(`/vendors/${vendorCode}/background`),
    getSrArticle: (vendorWare: string) =>
        request<any[]>(`/reports/sr-article/${encodeURIComponent(vendorWare)}`),
    getSrBrandWareGroup: (vendorBrand: string, wareCategory: string) =>
        request<any[]>(
            `/reports/sr-brand-ware-group/${encodeURIComponent(vendorBrand)}/${encodeURIComponent(wareCategory)}`,
        ),

    // Excel
    uploadExcel: (file: File, offerCode?: number) => {
        const formData = new FormData();
        formData.append('file', file);
        if (offerCode) formData.append('offer_code', String(offerCode));
        return request<{
            tmp_path: string;
            sheet_names: string[];
            file_name: string;
        }>('/excel/upload', {
            method: 'POST',
            body: formData,
        });
    },
    parseSheet: (
        tmpPath: string,
        sheetName: string,
        vendorCode?: number,
        offerCode?: number,
    ) =>
        request<{
            headers: string[];
            data: any[][];
            header_row: number;
            total_data_rows: number;
            row_indices?: number[];
            cached_mapping: Record<string, number> | null;
            auto_mapping: Record<string, number>;
            image_map?: Record<string, string>;
        }>('/excel/parse-sheet', {
            method: 'POST',
            body: JSON.stringify({
                tmp_path: tmpPath,
                sheet_name: sheetName,
                vendor_code: vendorCode,
                offer_code: offerCode,
            }),
        }),
    parseAllRows: (tmpPath: string, sheetName: string, offerCode?: number) =>
        request<{
            headers: string[];
            data: any[][];
            total_data_rows: number;
            row_indices?: number[];
            image_map?: Record<string, string>;
        }>('/excel/parse-all', {
            method: 'POST',
            body: JSON.stringify({
                tmp_path: tmpPath,
                sheet_name: sheetName,
                offer_code: offerCode,
            }),
        }),
    saveMapping: (
        headers: string[],
        mapping: Record<string, number>,
        vendorCode?: number,
    ) =>
        request<any>('/excel/save-mapping', {
            method: 'POST',
            body: JSON.stringify({ headers, mapping, vendor_code: vendorCode }),
        }),
    cleanupExcel: (tmpPath: string) =>
        request<any>('/excel/cleanup', {
            method: 'POST',
            body: JSON.stringify({ tmp_path: tmpPath }),
        }),

    // Report export (generated in background)
    startReportExport: (code: number) =>
        request<IReportExportJob>(`/offers/${code}/report-export`, { method: 'POST' }),
    getReportExport: (id: string) => request<IReportExportJob>(`/report-exports/${id}`),
};

export interface IReportExportJob {
    id: string;
    status: 'pending' | 'running' | 'done' | 'error';
    stage: string;
    error: string | null;
}

export { ApiError };
